import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  Eye,
  EyeOff,
  LayoutTemplate,
  Paintbrush,
  Plus,
  Save,
  Trash2,
  Undo2,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useFieldArray, useForm, useWatch, type FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useBlocker } from 'react-router'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormActions, FormSection } from '@/components/forms/FormSection'
import { FormField } from '@/components/forms/FormField'
import { toast } from '@/components/feedback/toast-store'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Dialog } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { ToggleRow } from '@/features/lms/components/ToggleRow'
import { tAny } from '@/i18n'
import { cn } from '@/utils/cn'
import { publicAdmissionsQuery, useSaveHomepage } from '../../api'
import {
  BLOCK_META,
  MAX_BLOCKS,
  newBlock,
  previewPage,
  toDraft,
  toPayload,
  type HomepageDraft,
} from '../../blocks'
import { homepageSchema } from '../../schemas'
import { AUTO_BLOCK_TYPES, BLOCK_TYPES, type BlockType, type HomepageEditorData } from '../../types'
import { HomepageView } from '../HomepageView'
import { BlockFields } from './BlockFields'
import { HomepagePreview } from './HomepagePreview'
import { PictureUpload } from './PictureUpload'

const FIELDS = ['enabled', 'heroTitle', 'heroSubtitle', 'coverFileId', 'accentColor', 'blocks'] as const

/** Settings → Homepage: the school's public page, block by block, with a live preview. */
export function HomepageEditor({ data }: { data: HomepageEditorData }) {
  const { t } = useTranslation('homepage')
  const save = useSaveHomepage()
  const admissions = useQuery(publicAdmissionsQuery())
  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<HomepageDraft>({ resolver: zodResolver(homepageSchema), defaultValues: toDraft(data) })
  // Blocks have their own "id"; the field array keeps its keys apart.
  const blocks = useFieldArray({ control, name: 'blocks', keyName: 'key' })
  const draft = useWatch({ control }) as HomepageDraft
  const [open, setOpen] = useState<Set<string>>(() => new Set())
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<number | null>(null)
  const listRef = useRef<HTMLOListElement>(null)

  const preview = useMemo(() => previewPage(draft, data.context), [draft, data.context])

  // Unsaved changes: warn before leaving the page (in the app or by closing the tab).
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && !isSubmitting && currentLocation.pathname !== nextLocation.pathname,
  )
  useEffect(() => {
    if (!isDirty) return
    const warn = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [isDirty])

  const toggleOpen = (id: string, value?: boolean) =>
    setOpen((prev) => {
      const next = new Set(prev)
      if (value ?? !next.has(id)) next.add(id)
      else next.delete(id)
      return next
    })

  const move = (from: number, to: number, direction: 'up' | 'down') => {
    const id = blocks.fields[from]?.id
    blocks.move(from, to)
    // Keep the keyboard on the moved block's button.
    requestAnimationFrame(() => {
      const button = listRef.current?.querySelector<HTMLButtonElement>(
        `[data-block="${id}"] [data-move="${direction}"]:not(:disabled)`,
      )
      ;(
        button ?? listRef.current?.querySelector<HTMLButtonElement>(`[data-block="${id}"] [data-move]`)
      )?.focus()
    })
  }

  const add = (type: BlockType) => {
    const block = newBlock(
      type,
      blocks.fields.map((b) => b.id),
    )
    blocks.append(block, { shouldFocus: false })
    toggleOpen(block.id, true)
    setAdding(false)
  }

  const onInvalid = (errs: FieldErrors<HomepageDraft>) => {
    const failing = (errs.blocks ?? []) as unknown[]
    failing.forEach((err, i) => {
      const id = draft.blocks[i]?.id
      if (err && id) toggleOpen(id, true)
    })
    toast.error(t('Please check the form'), t('Some blocks need your attention.'))
  }

  const submit = handleSubmit(async (values) => {
    try {
      const saved = await save.mutateAsync(toPayload(values))
      reset(toDraft(saved))
      toast.success(
        t('Homepage saved'),
        saved.enabled ? t('Visitors see the new version.') : t('The page is switched off.'),
      )
    } catch (e) {
      if (applyServerErrors(e, setError, FIELDS)) {
        // Server errors point at blocks by position: open them.
        draft.blocks.forEach((b) => toggleOpen(b.id, true))
      }
    }
  }, onInvalid)

  const presentAuto = new Set(
    draft.blocks.filter((b) => AUTO_BLOCK_TYPES.includes(b.type)).map((b) => b.type),
  )
  const blockErrors = errors.blocks
  const listError = errors.blocks?.message ?? errors.blocks?.root?.message

  return (
    <form onSubmit={(e) => void submit(e)} noValidate>
      <div className="grid grid-cols-1 gap-6 2xl:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        <div className="min-w-0 space-y-6">
          <FormSection title={t('Page')} icon={<LayoutTemplate />} columns={1}>
            <Controller
              control={control}
              name="enabled"
              render={({ field }) => (
                <ToggleRow
                  label={t('Show the homepage')}
                  hint={t('On: the school’s address opens this page. Off: it opens the sign-in page.')}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <FormField
              label={t('Main title')}
              error={errors.heroTitle?.message}
              hint={t('The school’s name when empty.')}
            >
              <Input {...register('heroTitle')} placeholder={data.context.school.name} maxLength={120} />
            </FormField>
            <FormField
              label={t('Subtitle')}
              error={errors.heroSubtitle?.message}
              hint={t('The motto when empty.')}
            >
              <Textarea
                {...register('heroSubtitle')}
                rows={2}
                maxLength={300}
                placeholder={data.context.school.motto ?? undefined}
              />
            </FormField>
            <Controller
              control={control}
              name="cover"
              render={({ field }) => (
                <div className="min-w-0">
                  <p className="mb-1.5 text-[0.8125rem] font-medium text-ink-soft">{t('Cover picture')}</p>
                  <div className="flex flex-wrap items-start gap-4">
                    {field.value?.url ? (
                      <img
                        src={field.value.url}
                        alt=""
                        className="aspect-[16/9] w-48 rounded-control border border-line object-cover"
                      />
                    ) : null}
                    <div className="space-y-2">
                      <PictureUpload
                        label={field.value ? t('Change the cover') : t('Add a cover')}
                        onUploaded={(img) => field.onChange(img)}
                      />
                      {field.value ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Trash2 />}
                          onClick={() => field.onChange(null)}
                        >
                          {t('Remove the cover')}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}
            />
            <Controller
              control={control}
              name="accentColor"
              render={({ field }) => (
                <FormField
                  label={t('Main colour')}
                  error={errors.accentColor?.message}
                  hint={t('Optional. Choose a colour dark enough for white text.')}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="color"
                      aria-label={t('Pick a colour')}
                      value={/^#[0-9a-f]{6}$/i.test(field.value) ? field.value : '#4f46e5'}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="h-10 w-12 cursor-pointer rounded-control border border-line bg-surface p-1"
                    />
                    <Input
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      placeholder="#4f46e5"
                      className="w-32"
                      aria-label={t('Colour code')}
                      maxLength={7}
                    />
                    {field.value ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<Paintbrush />}
                        onClick={() => field.onChange('')}
                      >
                        {t('Default colours')}
                      </Button>
                    ) : null}
                  </div>
                </FormField>
              )}
            />
          </FormSection>

          <section
            aria-labelledby="hp-blocks-title"
            className="rounded-card border border-line bg-surface shadow-card"
          >
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4">
              <div className="min-w-0">
                <h2 id="hp-blocks-title" className="text-[0.9375rem] font-semibold">
                  {t('Blocks')}
                </h2>
                <p className="mt-0.5 text-xs text-muted">{t('Shown from top to bottom, in this order.')}</p>
              </div>
              <Button
                size="sm"
                leftIcon={<Plus />}
                onClick={() => setAdding(true)}
                disabled={blocks.fields.length >= MAX_BLOCKS}
              >
                {t('Add a block')}
              </Button>
            </div>
            {blocks.fields.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-muted">
                {t('No blocks yet. Add a first one: a presentation, your classes, admissions…')}
              </p>
            ) : (
              <ol ref={listRef} className="divide-y divide-line">
                {blocks.fields.map((field, i) => {
                  const current = draft.blocks[i] ?? field
                  const meta = BLOCK_META[field.type]
                  const Icon = meta.icon
                  const title = current.title?.trim() || tAny(meta.defaultTitle)
                  const expanded = open.has(field.id)
                  const hasError = Boolean(blockErrors?.[i])
                  const panelId = `hp-block-${field.id}`
                  return (
                    <li
                      key={field.key}
                      data-block={field.id}
                      className={cn('px-4 py-3 sm:px-5', !current.visible && 'bg-surface-muted')}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => toggleOpen(field.id)}
                          aria-expanded={expanded}
                          aria-controls={panelId}
                          className="flex min-w-0 flex-1 items-center gap-3 rounded-control py-1 text-left"
                        >
                          <span
                            className={cn(
                              'grid size-9 shrink-0 place-items-center rounded-control bg-brand-50 text-brand-600',
                              !current.visible && 'opacity-50',
                            )}
                          >
                            <Icon className="size-4" aria-hidden="true" />
                          </span>
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-medium">{title}</span>
                            <span className="flex flex-wrap items-center gap-1.5 text-xs text-muted">
                              {tAny(meta.label)}
                              {!current.visible ? <Badge size="sm">{t('Hidden')}</Badge> : null}
                              {hasError ? (
                                <Badge size="sm" tone="danger">
                                  {t('To check')}
                                </Badge>
                              ) : null}
                            </span>
                          </span>
                          <ChevronDown
                            className={cn(
                              'ml-auto size-4 shrink-0 text-muted transition-transform',
                              expanded && 'rotate-180',
                            )}
                            aria-hidden="true"
                          />
                        </button>
                        <div className="flex shrink-0 items-center">
                          <IconButton
                            size="icon-sm"
                            data-move="up"
                            label={t('Move “{{title}}” up', { title })}
                            icon={<ArrowUp />}
                            disabled={i === 0}
                            onClick={() => move(i, i - 1, 'up')}
                          />
                          <IconButton
                            size="icon-sm"
                            data-move="down"
                            label={t('Move “{{title}}” down', { title })}
                            icon={<ArrowDown />}
                            disabled={i === blocks.fields.length - 1}
                            onClick={() => move(i, i + 1, 'down')}
                          />
                          <IconButton
                            size="icon-sm"
                            label={
                              current.visible
                                ? t('Hide “{{title}}”', { title })
                                : t('Show “{{title}}”', { title })
                            }
                            icon={current.visible ? <Eye /> : <EyeOff />}
                            aria-pressed={!current.visible}
                            onClick={() =>
                              setValue(`blocks.${i}.visible`, !current.visible, { shouldDirty: true })
                            }
                          />
                          <IconButton
                            size="icon-sm"
                            label={t('Delete “{{title}}”', { title })}
                            icon={<Trash2 />}
                            onClick={() => setRemoving(i)}
                          />
                        </div>
                      </div>
                      {expanded ? (
                        <div id={panelId} className="mt-3 pb-2">
                          <BlockFields
                            index={i}
                            type={field.type}
                            control={control}
                            register={register}
                            errors={blockErrors}
                          />
                        </div>
                      ) : null}
                    </li>
                  )
                })}
              </ol>
            )}
            {listError ? (
              <p className="px-5 pb-4 text-xs font-medium text-danger-600" role="alert">
                {tAny(listError)}
              </p>
            ) : null}
          </section>
        </div>

        <div className="min-w-0 2xl:sticky 2xl:top-20 2xl:self-start">
          <HomepagePreview>
            <HomepageView page={preview} admissions={admissions.isError ? null : admissions.data} preview />
          </HomepagePreview>
        </div>
      </div>

      <FormActions>
        <Button
          variant="secondary"
          leftIcon={<Undo2 />}
          disabled={!isDirty || isSubmitting}
          onClick={() => reset()}
        >
          {t('Discard changes')}
        </Button>
        <Button type="submit" leftIcon={<Save />} loading={isSubmitting} disabled={!isDirty}>
          {t('Save the homepage')}
        </Button>
      </FormActions>

      <Dialog
        open={adding}
        onOpenChange={setAdding}
        title={t('Add a block')}
        description={t('Choose what the new block shows. You can change its content right after.')}
      >
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {BLOCK_TYPES.map((type) => {
            const meta = BLOCK_META[type]
            const Icon = meta.icon
            const taken = presentAuto.has(type)
            return (
              <li key={type}>
                <button
                  type="button"
                  disabled={taken}
                  onClick={() => add(type)}
                  className="flex h-full w-full items-start gap-3 rounded-control border border-line p-3 text-left hover:border-brand-300 hover:bg-brand-50/40 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="grid size-9 shrink-0 place-items-center rounded-control bg-brand-50 text-brand-600">
                    <Icon className="size-4" aria-hidden="true" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{tAny(meta.label)}</span>
                    <span className="block text-xs text-muted">
                      {taken ? t('Already on the page.') : tAny(meta.description)}
                    </span>
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      </Dialog>

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(v) => (v ? undefined : setRemoving(null))}
        title={t('Delete this block?')}
        description={t('Its content will be lost when you save.')}
        confirmLabel={t('Delete')}
        onConfirm={() => {
          if (removing !== null) blocks.remove(removing)
          setRemoving(null)
        }}
      />

      <ConfirmDialog
        open={blocker.state === 'blocked'}
        onOpenChange={(v) => (v ? undefined : blocker.reset?.())}
        title={t('Leave without saving?')}
        description={t('Your changes to the homepage will be lost.')}
        confirmLabel={t('Leave the page')}
        cancelLabel={t('Stay')}
        onConfirm={() => blocker.proceed?.()}
      />
    </form>
  )
}
